import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchProductDetails } from './facility-product-dtails-apis';

// Row interface for API parameters
interface ProductRow {
  facilityId: string;
  productId: string;
}

// Props interface for RenderDetailPanel
interface RenderDetailPanelProps {
  row: { original: ProductRow };
}

// Column definitions for Product Details table
const productColumns = [
  { accessorKey: 'productNo', header: 'Product No.', minSize: 60, maxSize: 150 },
  { 
    accessorKey: 'associatedFacilityNo', 
    header: 'Associated Facility No.', 
    minSize: 60, 
    maxSize: 150,
    Cell: ({ cell }: { cell: { getValue: () => string | string[] } }) => {
      const value = cell.getValue();
      return Array.isArray(value) ? value.join(', ') : value;
    }
  },
  { accessorKey: 'standardLabelGrade', header: 'Standard Label Grade', minSize: 60, maxSize: 150 },
  { accessorKey: 'scProductStandardLabelGradeDescription', header: 'Standard Label Grade Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'productCategory', header: 'Product Category', minSize: 60, maxSize: 150 },
  { accessorKey: 'scProductCategoryCodeDescription', header: 'Product Category Description', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'productCategoryUserSpecificTerm',
    header: 'Product Category (User Specific Term)',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'productDetail', header: 'Product Detail', minSize: 60, maxSize: 150 },
  { accessorKey: 'scProductDetailCodeDescription', header: 'Product Detail Description', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'productDetailUserSpecificTerm',
    header: 'Product Detail (User Specific Term)',
    minSize: 60,
    maxSize: 150,
  },
];

// Column definitions for Raw Material Details table (Product)
const rawMaterialColumns = [
  {
    accessorKey: 'rawMaterialCode',
    header: 'Product Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'rawMaterialPercentage',
    header: 'Product Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'scProductRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

// Column definitions for Component Details table
const componentColumns = [
  { accessorKey: 'productComponentNo', header: 'Product Component No.', minSize: 60, maxSize: 150 },
  { accessorKey: 'componentDetail', header: 'Component Detail', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'componentStandardLabelGrade',
    header: 'Standard Label Grade',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'scComponentStandardLabelGradeDescription', 
    header: 'Standard Label Grade Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

// Column definitions for Component Raw Material Details table
const componentRawMaterialColumns = [
  {
    accessorKey: 'productComponentNo',
    header: 'Product Component No.',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'componentRawMaterialCode',
    header: 'Component Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'componentRawMaterialPercentage',
    header: 'Component Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'scComponentRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150,
  },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  // Extract SC_ID from URL parameters and facilityId/productId from row.original
  const { SC_ID } = useParams<{ SC_ID: string }>();
  const { facilityId, productId } = row.original;
  const { loading, executeGet } = useApi();
  const [productData, setProductData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    console.log("SC_ID:", SC_ID, "facilityId:", facilityId, "productId:", productId);
    if (!SC_ID || !facilityId || !productId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    console.log(facilityId)// ['01JQR08QJG40CEE0VZ13SYCQ7T', '01JQR06BS4KR4HW8QN7E6XJNBV']
    // Convert array to comma-separated string if it's an array
    fetchProductDetails(executeGet, SC_ID, facilityId, productId)
      .then((data) => setProductData(data?.data))
      .catch((err) => setError(err.message));
  }, [SC_ID, facilityId, productId, executeGet]);

  if (loading) return <LoaderComponent />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!productData) return <Typography>Loading product details...</Typography>;

  // Use the first object from facilityProductDetails as product info
  const productDetailObj = productData.facilityProductDetails?.[0] || {};
  console.log("productDetailObj------------>",productDetailObj.associatedFacilityNo)
  // Map Product Details
  const mappedProductDetails = {
    productNo: productDetailObj.scProductNo,
    associatedFacilityNo: productDetailObj.associatedFacilityNo,
    standardLabelGrade: Array.isArray(productDetailObj.scProductStandardLabelGrade)
      ? productDetailObj.scProductStandardLabelGrade.join(", ")
      : productDetailObj.scProductStandardLabelGrade,
    scProductStandardLabelGradeDescription: Array.isArray(productDetailObj.scProductStandardLabelGradeDescription)
      ? productDetailObj.scProductStandardLabelGradeDescription.join(", ")
      : productDetailObj.scProductStandardLabelGradeDescription,
    productCategory: productDetailObj.scProductCategoryCode,
    scProductCategoryCodeDescription: productDetailObj.scProductCategoryCodeDescription,
    productCategoryUserSpecificTerm: productDetailObj.scProductCategoryUserSpecificTerm,
    productDetail: productDetailObj.scProductDetailCode,
    scProductDetailCodeDescription: productDetailObj.scProductDetailCodeDescription,
    productDetailUserSpecificTerm: productDetailObj.scProductDetailUserSpecificTerm,
  };

  console.log("mappedProductDetails------------>",mappedProductDetails)

  // Map Raw Material Details (product level)
  const rawMaterialDetails = (productDetailObj.facilityProductRawMaterialDetails || []).map((item: any) => ({
    rawMaterialCode: item.scProductRawMaterialCode,
    rawMaterialPercentage: item.scProductRawMaterialPercentage,
    scProductRawMaterialCodeDescription: item.scProductRawMaterialCodeDescription
  }));

  // Map Component Details: take first component from scProductComponentDetails array.
  const pdctComponentDetails = productDetailObj.scProductComponentDetails || [];
  const mappedComponentDetails = pdctComponentDetails?.map((componentDetailObj:any)=>{return {
    productComponentNo: componentDetailObj.scProductComponentNo,
    componentDetail: componentDetailObj.scComponentDetail,
    componentStandardLabelGrade: Array.isArray(componentDetailObj.scComponentStandardLabelGrade)
      ? componentDetailObj.scComponentStandardLabelGrade.join(", ")
      : componentDetailObj.scComponentStandardLabelGrade,
      scComponentStandardLabelGradeDescription: Array.isArray(componentDetailObj.scComponentStandardLabelGradeDescription)
      ? componentDetailObj.scComponentStandardLabelGradeDescription.join(", ")
      : componentDetailObj.scComponentStandardLabelGradeDescription,
  };})

  // Map Component Raw Material Details
  const componentRawMaterialDetails = pdctComponentDetails?.flatMap((comp:any)=>
    comp?.scProductComponentRawMaterialDetails.map(
    (rawMaterial: {
      scComponentRawMaterialCode: string;
      scComponentRawMaterialPercentage: string;
    }) => ({
      productComponentNo:comp?.scProductComponentNo,
      componentRawMaterialCode: rawMaterial.scComponentRawMaterialCode,
      componentRawMaterialPercentage: String(
        rawMaterial.scComponentRawMaterialPercentage
      ),
    })
  ) || [])
  // (pdctComponentDetails.scProductComponentRawMaterialDetails || []).map(
  //   (item: any) => ({
  //     componentRawMaterialCode: item.scComponentRawMaterialCode,
  //     componentRawMaterialPercentage: item.scComponentRawMaterialPercentage,
  //     scComponentRawMaterialCodeDescription: item.scComponentRawMaterialCodeDescription
  //   })
  // );

  console.log("Mapped Component Details:", mappedComponentDetails);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Product Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Product Details
        </Typography>
        <CustomTable
          columns={productColumns}
          data={[mappedProductDetails]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Raw Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Raw Material Details
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialDetails}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Component Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Component Details
        </Typography>
        <CustomTable
          columns={componentColumns}
          data={mappedComponentDetails}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Component Raw Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Component Raw Material Details
        </Typography>
        <CustomTable
          columns={componentRawMaterialColumns}
          data={componentRawMaterialDetails}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
